import React, { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../supabase';
import { Icon } from './Icon.js';
import ProtocolShield from './ProtocolShield';
import BiohackInfoCard from './BiohackInfoCard';
import { useProtocolActions } from '../hooks/useProtocolActions';
import { useUserProtocolStatus } from '../contexts/UserProtocolStatusContext';
import { dedupeRequest, getCacheKey } from '../utils/queryDeduplication';
import './ShieldsPickerModal.css';

const ShieldsPickerModal = ({ onClose, onSelectProtocol, onPopulateBuilder }) => {
  const [protocols, setProtocols] = useState([]);
  const [biohacks, setBiohacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copyingProtocolId, setCopyingProtocolId] = useState(null);
  const [selectedBiohack, setSelectedBiohack] = useState(null);
  
  const { hasProtocol, refresh: refreshProtocolStatuses } = useUserProtocolStatus();
  const {
    userVotes,
    handleVote: handleVoteAction,
    fetchUserVotes
  } = useProtocolActions();

  // Fetch user votes on mount
  useEffect(() => {
    fetchUserVotes();
  }, [fetchUserVotes]);

  // Track if we're currently fetching biohacks to prevent duplicate calls
  const isFetchingBiohacksRef = useRef(false);

  // Fetch biohacks with deduplication
  useEffect(() => {
    // Prevent duplicate concurrent calls
    if (isFetchingBiohacksRef.current) {
      return;
    }

    const fetchBiohacks = async () => {
      isFetchingBiohacksRef.current = true;
      try {
        const biohacksCacheKey = getCacheKey('ShieldsPickerModal_fetchBiohacks', 'all');
        const data = await dedupeRequest(biohacksCacheKey, async () => {
          const { data, error } = await supabase
            .from('biohacks')
            .select('id, name, image_url, ease_of_use_score, time_efficiency_score, affordability_score');
          if (error) throw error;
          return data;
        });
        setBiohacks(data || []);
      } catch (err) {
        console.error('Error fetching biohacks:', err);
        setError('Failed to load biohacks.');
      } finally {
        isFetchingBiohacksRef.current = false;
      }
    };
    fetchBiohacks();
  }, []);

  // Track if we're currently fetching to prevent duplicate calls
  const isFetchingRef = useRef(false);

  // Fetch protocols
  const fetchProtocols = useCallback(async () => {
    if (biohacks.length === 0) return;
    
    // Prevent duplicate concurrent calls
    if (isFetchingRef.current) {
      return;
    }

    isFetchingRef.current = true;
    setLoading(true);
    setError(null);
    
    try {
      // Get all admin user IDs with deduplication
      const adminCacheKey = getCacheKey('ShieldsPickerModal_fetchAdmins', 'all');
      const admins = await dedupeRequest(adminCacheKey, async () => {
        const { data, error } = await supabase
          .from('user_profiles')
          .select('user_id, username, admin_flag')
          .eq('admin_flag', 1);
        if (error) throw error;
        return data;
      });
      
      const adminIds = (admins || []).map(a => a.user_id);
      
      if (adminIds.length === 0) {
        setProtocols([]);
        setLoading(false);
        isFetchingRef.current = false;
        return;
      }
      
      // Get all protocols created by admins with deduplication
      const protocolsCacheKey = getCacheKey('ShieldsPickerModal_fetchProtocols', adminIds.join(','));
      const data = await dedupeRequest(protocolsCacheKey, async () => {
        const { data, error } = await supabase
          .from('protocols')
          .select(`
            *, 
            protocol_hacks ( 
              hack_id, 
              position,
              biohacks (
                id,
                name,
                image_url,
                category
              )
            ),
            user_profiles!protocols_created_by_fkey (
              user_id,
              username
            )
          `)
          .in('created_by', adminIds);
        if (error) throw error;
        return data;
      });
      
      // Join protocol_hacks to biohacks
      const protocolsWithHacks = (data || []).map(protocol => {
        const hacks = (protocol.protocol_hacks || [])
          .sort((a, b) => a.position - b.position)
          .map(h => {
            const foundBiohack = biohacks.find(b => b.id === h.hack_id);
            return foundBiohack;
          })
          .filter(Boolean);
        
        const creatorUsername = protocol.user_profiles?.username || 
                               admins.find(admin => admin.user_id === protocol.created_by)?.username || 
                               'Unknown Creator';
        
        return { 
          ...protocol, 
          hacks,
          created_by_username: creatorUsername
        };
      });
      
      setProtocols(protocolsWithHacks);
    } catch (err) {
      setError('Failed to load protocols.');
      console.error('Error fetching protocols:', err);
    } finally {
      setLoading(false);
      isFetchingRef.current = false;
    }
  }, [biohacks]);

  useEffect(() => {
    if (biohacks.length > 0) {
      fetchProtocols();
    }
    // fetchProtocols is a useCallback that depends on biohacks, so it will use the latest biohacks
    // The isFetchingRef prevents duplicate concurrent calls
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [biohacks.length]);

  // Handle protocol like/dislike
  const handleLike = async (protocol) => {
    await handleVoteAction(
      protocol,
      1,
      () => {
        // Success callback - vote state is managed by the hook
      },
      (errorMsg) => {
        console.error('Error liking protocol:', errorMsg);
      }
    );
  };

  const handleDislike = async (protocol) => {
    await handleVoteAction(
      protocol,
      -1,
      () => {
        // Success callback - vote state is managed by the hook
      },
      (errorMsg) => {
        console.error('Error disliking protocol:', errorMsg);
      }
    );
  };

  // Handle protocol copy - now populates builder instead of adding to profile
  const handleProtocolCopy = async (protocol) => {
    setCopyingProtocolId(protocol.id);
    
    try {
      // Check if protocol has hacks
      if (!protocol.hacks || protocol.hacks.length === 0) {
        alert('Cannot use a protocol with no biohacks');
        setCopyingProtocolId(null);
        return;
      }
      
      // If onPopulateBuilder is provided, use it to populate the builder bar
      if (onPopulateBuilder) {
        onPopulateBuilder(protocol.hacks);
        onClose();
      } else {
        // Fallback to old behavior if onPopulateBuilder is not provided
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) {
          alert('Please sign in to add protocols');
          setCopyingProtocolId(null);
          return;
        }
        
        if (hasProtocol(protocol.id)) {
          alert('You already have this protocol in your profile');
          setCopyingProtocolId(null);
          return;
        }
        
        const { error: statusError } = await supabase
          .from('protocols_status')
          .insert({
            user_id: user.id,
            protocol_id: protocol.id,
            is_active: false,
            added_at: new Date().toISOString()
          });

        if (statusError) {
          if (statusError.code === '23505') {
            await refreshProtocolStatuses();
            alert('You already have this protocol in your profile');
            setCopyingProtocolId(null);
            return;
          }
          throw statusError;
        }
        
        await refreshProtocolStatuses();
        alert('Protocol added to your profile!');
        if (onSelectProtocol) {
          onSelectProtocol(protocol);
        }
        onClose();
      }
    } catch (error) {
      console.error('Error copying protocol:', error);
      alert('Error adding protocol. Please try again.');
    } finally {
      setCopyingProtocolId(null);
    }
  };

  return (
    <div className="shields-picker-overlay" onClick={onClose}>
      <div className="shields-picker-modal" onClick={(e) => e.stopPropagation()}>
        <div className="shields-picker-header">
          <h3>Choose a Protocol Made by Cetarus</h3>
          <button className="shields-picker-close" onClick={onClose}>
            <Icon name="X" className="icon--md" />
          </button>
        </div>
        
        <div className="shields-picker-content">
          {loading ? (
            <div className="shields-picker-loading">
              <p>Loading protocols...</p>
            </div>
          ) : error ? (
            <div className="shields-picker-error">
              <p>{error}</p>
            </div>
          ) : protocols.length === 0 ? (
            <div className="shields-picker-empty">
              <p>No protocols available</p>
            </div>
          ) : (
            <div className="shields-grid">
              {protocols.map((protocol) => (
                <div 
                  key={protocol.id} 
                  className="shield-item"
                  onClick={() => handleProtocolCopy(protocol)}
                >
                  <ProtocolShield 
                    protocol={protocol} 
                    size="medium" 
                    onBiohackClick={setSelectedBiohack}
                    onLike={handleLike}
                    onDislike={handleDislike}
                    userVote={userVotes[protocol.id] || null}
                  />
                  <button 
                    className="shield-add-button"
                    disabled={copyingProtocolId === protocol.id}
                  >
                    <Icon name={copyingProtocolId === protocol.id ? "Loader" : "Plus"} className="icon--sm" />
                    <span>{copyingProtocolId === protocol.id ? 'Adding...' : onPopulateBuilder ? 'Add to Builder' : 'Add to Profile'}</span>
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Biohack Info Card Modal */}
      {selectedBiohack && (
        <BiohackInfoCard
          biohack={selectedBiohack}
          onClose={() => setSelectedBiohack(null)}
          onRatingSubmitted={() => {
            // Refresh data if needed
            console.log('Rating submitted for biohack from shields picker');
          }}
        />
      )}
    </div>
  );
};

export default ShieldsPickerModal;

